import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getResellerStats } from '@/modules/Reseller/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import Paper from '@/components/Paper'
import { Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import ChartIcons from '@/icons/ChartIcons'
import ServiceIcons from '@/icons/ServiceIcons'
import BadgeDollarIcons from '@/icons/BadgeDollarIcons'

interface StatsProps {
    resellerId: number
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        maximumFractionDigits: 0,
    }).format(value)
}

const formatNumber = (value: number) => {
    return new Intl.NumberFormat('vi-VN').format(value)
}

interface StatItemProps {
    label: string
    value: string | number
    subValue?: string
    icon?: React.ReactNode
    color?: string
    loading?: boolean
}

const StatItem: React.FC<StatItemProps> = ({
    label,
    value,
    subValue,
    icon,
    color = 'text-gray-900',
    loading,
}) => {
    if (loading) {
        return (
            <div>
                <Skeleton width={80} height={16} />
                <Skeleton width={100} height={28} className="mt-1" />
            </div>
        )
    }

    return (
        <div>
            <div className="flex items-center gap-1.5">
                {icon && (
                    <div className="w-4 h-4 flex-shrink-0 text-gray-400 [&>div]:w-full [&>div]:h-full [&_svg]:w-full [&_svg]:h-full">
                        {icon}
                    </div>
                )}
                <p className="text-sm text-gray-500">{label}</p>
            </div>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
            {subValue && (
                <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>
            )}
        </div>
    )
}

const Stats: React.FC<StatsProps> = ({ resellerId }) => {
    const { t } = useTranslation('reseller')

    const { data, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.RESELLERS, 'stats', resellerId],
        queryFn: () => getResellerStats(resellerId),
        enabled: !!resellerId,
        staleTime: 60000, // 1 minute
    })

    const stats = data?.result

    return (
        <Paper className="mt-4 p-4">
            <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 text-primary">
                    <ChartIcons />
                </div>
                <span className="font-semibold text-bg-primary text-sm">
                    {t('performance_summary')}
                </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {/* Services */}
                <StatItem
                    label={t('total_services')}
                    value={formatNumber(stats?.total_services || 0)}
                    subValue={`${formatNumber(stats?.active_services || 0)} ${t('active')}`}
                    icon={<ServiceIcons />}
                    loading={isLoading}
                />

                {/* MRR */}
                <StatItem
                    label={t('monthly_recurring')}
                    value={formatCurrency(stats?.mrr || 0)}
                    icon={<BadgeDollarIcons />}
                    color="text-blue-600"
                    loading={isLoading}
                />

                {/* Total Revenue */}
                <StatItem
                    label={t('total_revenue')}
                    value={formatCurrency(stats?.total_revenue || 0)}
                    icon={<BadgeDollarIcons />}
                    color="text-green-600"
                    loading={isLoading}
                />

                {/* Pending Revenue */}
                <StatItem
                    label={t('pending_revenue')}
                    value={formatCurrency(stats?.pending_revenue || 0)}
                    icon={<BadgeDollarIcons />}
                    color="text-orange-500"
                    loading={isLoading}
                />
            </div>
        </Paper>
    )
}

export default Stats
